import React from 'react'
import { broadcastPreferredLocale } from 'web/src/shared/Auth'
import { LocalizationProvider, ReactLocalization } from '@fluent/react'
import { canAccessWebLocalStorage } from '@mote/helpers'
import {
  DEFAULT_LOCALE,
  LOCALIZATION_LANGUAGES,
  PREFERRED_LOCALE_STORAGE_KEY
} from '@mote/helpers/constants'
import { usePreferredLocaleMutation } from '../api/usePreferredLocaleMutation'
import { generateTranslationBundles } from '../helpers/generateTranslationBundles'

function getPreferredLocaleFromStorage() {
  if (!canAccessWebLocalStorage()) return DEFAULT_LOCALE

  return (
    window.localStorage.getItem(PREFERRED_LOCALE_STORAGE_KEY) ||
    window.localStorage.getItem('recommendedUXLocale') ||
    DEFAULT_LOCALE
  )
}

function setPreferredLocaleInStorage(newLocale) {
  if (!canAccessWebLocalStorage()) return

  window.localStorage.setItem(PREFERRED_LOCALE_STORAGE_KEY, newLocale)
}

const LocaleContext = React.createContext({})

export const LocaleProvider = ({ children, initialL10n = null }) => {
  const [l10n, setL10n] = React.useState(initialL10n)
  const [preferredLocale, setPreferredLocale] = React.useState()

  async function getLocaleBundles() {
    const localeBundles = await generateTranslationBundles(preferredLocale)

    setL10n(new ReactLocalization(localeBundles))
  }

  React.useEffect(() => {
    if (!preferredLocale) return

    getLocaleBundles()
  }, [preferredLocale])

  React.useEffect(() => {
    if (preferredLocale) return

    const localeInStorage = getPreferredLocaleFromStorage()

    if (
      !localeInStorage ||
      (localeInStorage &&
        !LOCALIZATION_LANGUAGES.find(locale => locale.code === localeInStorage))
    ) {
      setPreferredLocaleInStorage(DEFAULT_LOCALE)
      return setPreferredLocale(DEFAULT_LOCALE)
    }

    setPreferredLocale(localeInStorage)
  }, [preferredLocale])

  if (!l10n || !preferredLocale) return <></>

  return (
    <LocaleContext.Provider value={[preferredLocale, setPreferredLocale]}>
      <LocalizationProvider l10n={l10n}>{children}</LocalizationProvider>
    </LocaleContext.Provider>
  )
}

export const useLocale = () => {
  const [preferredLocale, setPreferredLocale] = React.useContext(LocaleContext)
  const preferredLocaleMutation = usePreferredLocaleMutation()
  const currentLocale = LOCALIZATION_LANGUAGES.find(
    item => item.code === preferredLocale
  )

  async function updateLocale(newLocale) {
    setPreferredLocale(newLocale)
    await preferredLocaleMutation.mutate(newLocale)
    broadcastPreferredLocale(newLocale)
    setPreferredLocaleInStorage(newLocale)
  }

  return [currentLocale, updateLocale]
}
